import { useEffect, useState } from 'react';

export const REVEAL_STEP = 2;
export const REVEAL_INTERVAL_MS = 16;
export const CATCH_UP_TICKS = 12;

export function revealStep(shownLength: number, sourceLength: number): number {
	const backlog = Math.max(0, sourceLength - shownLength);
	return Math.max(REVEAL_STEP, Math.ceil(backlog / CATCH_UP_TICKS));
}

export function nextRevealedText(shown: string, source: string): string {
	if (source === shown) {
		return shown;
	}

	if (!source.startsWith(shown)) {
		return source.slice(0, Math.min(revealStep(0, source.length), source.length));
	}

	return source.slice(
		0,
		Math.min(shown.length + revealStep(shown.length, source.length), source.length),
	);
}

export function useSmoothedText(source: string, live: boolean): string {
	const [shown, setShown] = useState(live ? '' : source);
	// Snap or reset here, not in the interval Effect.
	// https://react.dev/learn/you-might-not-need-an-effect#adjusting-some-state-when-a-prop-changes
	const displayed = source.startsWith(shown) ? shown : live ? '' : source;
	if (displayed !== shown) {
		setShown(displayed);
	}

	const caughtUp = displayed === source;

	useEffect(() => {
		if (caughtUp) {
			return;
		}

		let id = 0;
		const tick = () => {
			setShown((current) => {
				const next = nextRevealedText(current, source);
				if (next === source) {
					window.clearInterval(id);
				}
				return next;
			});
		};

		id = window.setInterval(tick, REVEAL_INTERVAL_MS);
		tick();
		return () => window.clearInterval(id);
	}, [source, caughtUp]);

	return displayed;
}
